/**
 * 워크스페이스 끌어 놓기 — 무엇을 끌고 있는지 담는 자리 (2026-08-18).
 *
 * 표 안에서만 옮길 때는 컴포넌트 안의 ref 하나로 충분했다(#493). 좌측 폴더 트리에도
 * 놓을 수 있게 되면서 **다른 컴포넌트**가 같은 짐을 읽어야 한다 → `dataTransfer` 에 싣는다.
 *
 * ⚠ **`dragover` 에서는 `getData()` 가 빈 문자열을 준다.** 브라우저가 끌기 도중의 내용
 *   엿보기를 막기 때문이다(drop 순간에만 읽힌다). 그래서 "여기에 놓을 수 있는가" 는
 *   내용이 아니라 **`types` 에 우리 형식이 있는가**로 판단한다 — {@link hasWorkspaceNodes}.
 *
 * ⚠ 형식 이름은 소문자여야 한다. 브라우저가 `types` 를 소문자로 정규화해서, 대문자로
 *   넣으면 `types.includes('...')` 가 영영 거짓이 된다.
 */

/** 우리 짐임을 알리는 형식. 파일 탐색기에서 끌어온 파일(`Files`)과 구분하는 열쇠이기도 하다. */
export const WORKSPACE_NODES_MIME = 'application/x-workchat-workspace-nodes';

/** 끌기를 시작할 때 — 옮길 것들의 id 를 싣는다(이름은 사람이 보는 미리보기용). */
export function setWorkspaceNodes(dt: DataTransfer, ids: readonly string[], names: readonly string[]): void {
  dt.setData(WORKSPACE_NODES_MIME, JSON.stringify(ids));
  // 뭐라도 실어야 파이어폭스가 끌기를 시작한다 — 사람이 읽을 이름을 함께 준다.
  dt.setData('text/plain', names.join(', '));
}

/**
 * 파일 탐색기에서 끌어온 파일인가 (2026-08-19).
 *
 * 브라우저는 OS 파일 끌기에 `'Files'` 형식을 붙인다. 이걸 받아 주지 않으면(=preventDefault
 * 를 안 하면) 브라우저 기본 동작이 돌아 **그 파일을 열어 버린다** — 업로드가 아니라
 * 다운로드처럼 보이던 증상이 그것이다.
 */
export function hasExternalFiles(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types).includes('Files');
}

/** `drop` 순간에만 쓸 수 있다. 폴더를 끌어 놓으면 항목은 있어도 File 이 없어 걸러진다. */
export function readExternalFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  return Array.from(dt.files ?? []);
}

/** `dragover` 판정용 — 내용은 못 읽지만 형식은 볼 수 있다. */
export function hasWorkspaceNodes(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types).includes(WORKSPACE_NODES_MIME);
}

/** `drop` 순간에만 쓸 수 있다. 깨진 값이면 빈 배열(끌기가 조용히 실패하게 두지 않는다). */
export function readWorkspaceNodes(dt: DataTransfer | null): string[] {
  if (!dt) return [];
  try {
    const raw = dt.getData(WORKSPACE_NODES_MIME);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}
